<script setup lang="ts">
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'
import { Bar } from 'vue-chartjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useDevice } from "@/composables/use-device";
import { images } from "~/utilities/images";

const { isMobile, isTablet } = useDevice();

const props = withDefaults(defineProps<{ chartData: any }>(), {
  chartData: {
    labels: [],
    prices: [],
  },
});

if (import.meta.client) {
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels)
}

let preloadedImage: HTMLImageElement
if (import.meta.client) {
  preloadedImage = new Image()
  preloadedImage.src = images.shower
}

// Custom tooltip function – make sure it runs only on client
function customTooltip(context: any) {
  if (!import.meta.client) return
  let tooltipEl = document.getElementById('chartjs-tooltip')
  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.id = 'chartjs-tooltip'
    tooltipEl.style.position = 'absolute'
    tooltipEl.style.pointerEvents = 'none'
    tooltipEl.style.transition = 'opacity 0.3s ease'
    document.body.appendChild(tooltipEl)
  }
  const tooltipModel = context.tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = '0'
    return
  }
  let innerHtml = ''
  if (tooltipModel.title && tooltipModel.title.length) {
    innerHtml += `<div style="font-weight:bold; margin-bottom:4px;">${tooltipModel.title[0]}</div>`
  }
  if (tooltipModel.body) {
    const bodyLines = tooltipModel.body.map((b: any) => b.lines)
    innerHtml += bodyLines.join('<br>')
  }
  tooltipEl.innerHTML = innerHtml
  const canvasRect = context.chart.canvas.getBoundingClientRect()
  tooltipEl.style.opacity = '1'
  tooltipEl.style.left = canvasRect.left + window.scrollX + tooltipModel.caretX + 'px'
  tooltipEl.style.top = canvasRect.top + window.scrollY + tooltipModel.caretY + 'px'
  tooltipEl.style.background = '#03FEC0'
  tooltipEl.style.color = '#0A1E3F'
  tooltipEl.style.padding = '30px'
  tooltipEl.style.borderRadius = '50%'
  tooltipEl.style.transform = 'translate(-50%, -110%)'
}

const customImagePlugin = {
  id: 'customImagePlugin',
  afterDatasetsDraw(chart: any) {
    if (!import.meta.client) return

    if (!isTablet.value && !isMobile.value) {

      const { ctx } = chart
      const firstBar = chart.getDatasetMeta(0).data[0]
      if (!firstBar || !preloadedImage?.complete) return
      const { x, y, base } = firstBar.getProps(['x', 'y', 'base'], true)
      const barHeight = Math.abs(y - base)
      const barWidth = firstBar.width
      const imgWidth = barWidth * 1.2
      const imgHeight = barHeight * .9
      const imgX = x - imgWidth / 2
      const imgY = 200 - imgHeight / 2
      ctx.drawImage(preloadedImage, imgX, imgY, imgWidth, imgHeight)
    }
  }
}

if (import.meta.client) {
  ChartJS.register(customImagePlugin)
}

const data = {
  labels: props.chartData.labels,
  datasets: [
    {
      label: 'Öre/kWh',
      backgroundColor: ['#03FEC0','#3D6299','#3D6299','#3D6299','#3D6299'],
      data: props.chartData.prices,
    },
  ],
}

const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 30
    }
  },
  plugins: {
    tooltip: {
      enabled: false,
      external: customTooltip
    },
    legend: {
      display: false
    },
    datalabels: {
      anchor: 'end',
      color: '#03FEC0',
      align: 'top',
      clip: false,
      font: {
        size: 18
      }
    }
  },
  scales: {
    x: {
      ticks: {
        color: '#03FEC0',
        font: {
          size: 18,
        }
      },
      grid: { display: false },
      border: { display: false }
    },
    y: {
      ticks: { display: false },
      grid: { display: false },
      border: { display: false }
    }
  }
}
</script>

<template>
  <div class="h-[21rem]">
    <client-only>
      <Bar :data="data" :options="options" />
    </client-only>
  </div>
</template>

<style>
#chartjs-tooltip::after {
  content: "";
  position: absolute;
  top: 95%;
  left: 45%;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 18px 6px 0 7px;
  border-color: #03FEC0 transparent transparent transparent;
  transform: rotate(-6deg);
  background: transparent;
}
</style>
